import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { SubscriptionsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useRef, useState } from 'react';
import { Content } from './styles/Content';
import { RangeSliderWrapper } from './styles/RangeSliderWrapper';

type TProps = {
  billingAccountId: string;
  subscriptionId: string;
  subscriptionProductId: string;
  planName: string;
  price: number;
  freeSeats: number;
  onSuccess: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  licenses: number;
};

export const ConfirmSubscriptionUpgrade = ({
  billingAccountId,
  subscriptionId,
  subscriptionProductId,
  planName,
  price,
  freeSeats,
  onCancel,
  onSuccess,
  licenses = 1,
}: TProps) => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const requestErrorMessageRef = useRef<string | undefined>(undefined);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleError = (message?: string, result?: { error?: string }) => {
    let errorMessage = message ?? result?.error ?? 'Something went wrong. Please contact administrator.';
    if (result?.error === 'No payment methods found') {
      errorMessage = 'Cannot create subscription. Please add a payment method first.';
    }
    if (result?.error === 'Subscription already exists') {
      errorMessage = 'You already have this subscription. Please choose another or modify an existing subscription.';
    }
    onCancel();
    openModal(ModalNamesEnum.GenericErrorModal, {
      message: errorMessage,
    });
  };

  const upgradeMutation = useMutation({
    mutationFn: () => {
      requestErrorMessageRef.current = undefined;
      return call(
        () =>
          SubscriptionsService.upgrade({
            baId: billingAccountId,
            subscriptionId,
            requestBody: { subscriptionProductId: subscriptionProductId, licenseCount: licenses },
          }),
        {
          onError: (message) => {
            requestErrorMessageRef.current = message;
          },
        },
      );
    },
    onSuccess: (result) => {
      if (!result || !result.success) {
        handleError(undefined, result);
        return;
      }
      closeModal();
      onSuccess();
    },
    onError: () => {
      handleError(requestErrorMessageRef.current);
    },
  });

  const isSubmitting = upgradeMutation.isPending;

  return (
    <ModalWrapper width="480px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle data-testid="purchasePlan">Confirm</ModalTitle>
      <Content>
        <p className="description">
          You are about to purchase the &quot;{planName}&quot; plan for ${price.toFixed(2)} per license per month +
          applicable sales tax
        </p>

        <RangeSliderWrapper>
          <label htmlFor="steps-range" className="mutedLabel block mb-2 font-[600] text-xs text-left">
            Number of licenses:
          </label>
          <input
            id="steps-range"
            disabled={true}
            type="range"
            min={1}
            max={250}
            value={licenses}
            step={1}
            className="w-full h-2 bg-[var(--color-surface-muted)] rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">
            {licenses} license{licenses > 1 && 's'}
          </span>
        </RangeSliderWrapper>

        <div className="text-left my-4 flex flex-col">
          {freeSeats ? (
            <div>
              <span className="mutedLabel font-[600] text-xs">Free licenses in subscription plan:</span>
              <span className="float-right">{freeSeats}</span>
            </div>
          ) : null}
          <div>
            <span className="mutedLabel font-[600] text-xs">Total subscription price per month:</span>
            <span className="float-right">
              ${licenses <= freeSeats ? 0 : (Number(price) * (licenses - freeSeats)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="checkbox-wrapper">
          <CheckBox
            name="terms-accepted"
            checked={termsAccepted}
            onChange={(value) => setTermsAccepted(value)}
            testId="accept-terms-checkbox"
          />
          <label>
            I agree to the{' '}
            <a target="_blank" rel="noreferrer" href="/agreement.html">
              Terms of Service
            </a>
          </label>
        </div>

        <div className="buttonContainer">
          <OutlinedButton className="w-full" type="button" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <Button
            className="w-full"
            data-testid="submitButton"
            type="submit"
            onClick={() => upgradeMutation.mutate()}
            disabled={isSubmitting || !termsAccepted}
          >
            Upgrade
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
